import moment, { Moment, unitOfTime } from "moment";
import _ from "@/boot/lodash";
import store from "@/store";
import i18n from "@/i18n";
import { STATS_ALL_CURRENCY_CODE } from "@/data/constants";

export function isLeapYear(year) {
  return (year % 4 == 0 && year % 100 != 0) || year % 400 == 0;
}

export function getDaysInAYear(year) {
  return isLeapYear(year) ? 366 : 365;
}

export function calcGroup(dates: Date[]) {
  const last: Date | undefined = _.last(dates);
  // last = moment(last).isAfter(new Date()) ? new Date() : last; // ignore future dates as we won't have any data for them
  const first: Date | undefined = _.first(dates);
  const daysInAYear = getDaysInAYear(moment(first).year());
  const duration = moment.duration(moment(last).diff(first));
  let group = "day";
  const days = _.round(Math.abs(duration.asDays()), 0);
  if (days <= 1) {
    group = "hour";
  } else if (days > 1 && days <= 31) {
    group = "day";
  } else if (days > 31 && days <= 31 * 3) {
    group = "week";
  } else if (days > 31 * 3 && days <= daysInAYear) {
    group = "month";
  } else if (days > daysInAYear) {
    group = "year";
  }
  return group as unitOfTime.Base;
}

export function dateFormat(groupName: unitOfTime.Base, dates: Date[]): string {
  switch (groupName) {
    case "hour":
      return moment(dates[0]).isSame(dates[1], "day") ? "hh A" : "Do hh A";
    case "day":
      return moment(dates[0]).isSame(dates[1], "month") ? "Do" : "MMM Do";
    case "week":
      return i18n.t("_datetime.week.long") as string;
    case "month":
      return moment(dates[0]).isSame(dates[1], "year") ? "MMM" : "MMM YYYY";
    case "year":
      return "YYYY";
    default:
      return i18n.t("_datetime.date.long") as string;
  }
}

export function defaultChartTickMapFunc(
  item: any,
  date: Moment
): { [key: string]: any } {
  return item ? { value: item, date } : { value: 0, date };
}

export function getTickCountBetweenDates(
  group: unitOfTime.Base,
  dates: Date[]
) {
  const start = moment(dates[0]).startOf("day");
  let end = moment(dates[1]).endOf("day");

  // In order to include 12AM of next day, because any data after 11PM won't be displayed otherwise
  if (group === "hour") {
    end = moment(dates[1])
      .add(1, "day")
      .startOf("day");
  }

  return Math.abs(end.diff(start, group));
}

/**
 * Generates array with length equal the difference between @param dates.
 * If any date matches date in @existingDates it will use value from @existingValues with same index
 * @param group Group
 * @param rangeDates Date range
 * @param existingDates Array of dates @param existingValues are for. Must be equal in length with @existingValues
 * @param existingValues Array of values for @param existingDates. Must be equal in length with @existingDates
 * @param tickMapFunc Optional func to map each item. Defaults to { value: any date: Moment}
 */
export function dateRangeValues(
  group: unitOfTime.Base,
  rangeDates: Date[],
  existingDates: Date[],
  existingValues: any[],
  tickMapFunc = defaultChartTickMapFunc
) {
  const start = moment(rangeDates[0]).startOf("day");
  const tickCount = getTickCountBetweenDates(group, rangeDates);

  // Create array with dates for all ticks within the range and map to tick data
  return _.range(0, tickCount + 1).map(i => {
    const tickDate = start.clone().add(i, group);
    const dataIndex = _.findIndex(existingDates, date =>
      tickDate.isSame(date, group)
    );

    return tickMapFunc(
      dataIndex > -1 ? existingValues[dataIndex] : null,
      tickDate
    );
  });
}

/**
 * Converts from year and day of the year (NOT month) to js date
 * i.e. 2020 60 => 2020/03/01 60th day of 2020 is 1st of March
 * @param year Year
 * @param day Day of the year
 * @param hour Hour of the day
 */
export function convertToDate(year: number, day: number, hour?: number): Date {
  let date = new Date(year, 0); // initialize a date in `year-01-01`
  if (day) {
    date = new Date(date.setDate(day)); // add the number of days
  }
  if (hour) {
    date = new Date(date.setHours(hour)); // add the number of hours
  }
  return date;
}

/* Splits a number into equal parts
 * Example: 120 into 6 parts = [20, 20, 20, 20, 20, 20]
 */
export function splitToParts(value: number, parts = 6, base = 10) {
  const values: number[] = [];
  while (value > 0 && parts > 0) {
    let a = Math.ceil(value / parts / base) * base;
    if (a % 2 == 0) a = Math.floor(value / parts / base) * base;
    else a = Math.ceil(value / parts / base) * base;

    value -= a;
    parts--;
    values.push(a);
  }
  return values;
}

/**
 * Returns array of currencies in the result.
 * If result set is empty return  brand currency or default (GBP)
 * @example for object like { USD: {...} } it will return array ["USD"]
 */
export function getCurrencies(data: any): string[] {
  const currencies = _.get(store, "state.constants.currencies", []);
  const brandCurrencyId = _.get(store, "state.brand.currency_id");
  const brandCurrency = currencies.find(c => c.id === brandCurrencyId);
  const brandOrDefaultCurrency = _.get(
    brandCurrency,
    "code",
    STATS_ALL_CURRENCY_CODE
  );

  const currencyCodes = _.isEmpty(data)
    ? [_.toUpper(brandOrDefaultCurrency)]
    : _.map(_.orderBy(_.keys(data)), key => key.toUpperCase());
  const currencyCodesWithoutAll = _.filter(
    currencyCodes,
    i => i !== STATS_ALL_CURRENCY_CODE
  );
  return currencyCodesWithoutAll.length === 1
    ? currencyCodesWithoutAll
    : currencyCodesWithoutAll.length === 0
    ? [_.toUpper(brandOrDefaultCurrency)]
    : currencyCodes;
}

const colors: any = [
  {
    rgb: { r: 1, g: 143, b: 253 }
  },
  {
    rgb: { r: 42, g: 201, b: 3 }
  },
  {
    rgb: { r: 203, g: 61, b: 36 }
  },
  {
    rgb: { r: 218, g: 8, b: 155 }
  },
  {
    rgb: { r: 96, g: 252, b: 232 }
  },
  {
    rgb: { r: 73, g: 199, b: 104 }
  },
  {
    rgb: { r: 108, g: 203, b: 6 }
  }
];

_.range(15).forEach(() => {
  const r = _.random(0, 255);
  const g = _.random(0, 255);
  const b = _.random(0, 255);
  colors.push({
    rgb: { r, g, b }
  });
});

/**
 * Returns border and background color by index or default color (NULL) required from chart.js
 */
export function indexColor({ index, alpha = 1, bgAlpha = 0.25 }) {
  if (_.isNil(index))
    return {
      borderColor: null,
      backgroundColor: null
    };

  const color = colors?.[index];
  if (color) {
    return {
      borderColor: `rgb(${color.rgb.r}, ${color.rgb.g},${color.rgb.b}, ${alpha})`,
      backgroundColor: `rgb(${color.rgb.r}, ${color.rgb.g},${color.rgb.b}, ${bgAlpha})`
    };
  } else
    return {
      borderColor: null,
      backgroundColor: null
    };
}

export function formatCurrency(currency, value) {
  return currency
    ? `${value < 0 ? "-" : ""}${currency.prefix}${Math.abs(value)}${
        currency.suffix
      }`
    : value;
}

export function getYAxisWithCurrency(value) {
  // Include a currency sign in the ticks
  // Chart.js bind `this` to this function
  //@ts-ignore
  const currency = _.get(this, "chart.data.datasets[0].currency");
  return formatCurrency(currency, value);
}

export function getTooltipFormattedLabel(tooltipItem) {
  return _.get(
    tooltipItem,
    `dataset.mappedData[${tooltipItem.dataIndex}].valueFormatted`,
    tooltipItem.formattedValue
  );
}

/**
 * Retuns color for currency, if no currency color is found returns border and background color by index or default color (NULL) required from chart.js
 */
export function currencyColor(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  args
): {
  borderColor: string;
  backgroundColor: string;
} {
  return {
    borderColor: "rgb(1, 143, 253)",
    backgroundColor: "rgb(1, 143, 253, 0.25)"
  };
}

/**
 * Returns charts minimum value 10 if value is less that minimum
 * If value is 0 return 0
 */
export function getValueOrChartDefaultMinMax(value) {
  return value && value < 10 ? 10 : value;
}

const baseList = [
  { min: 10, base: 5 },
  { min: 100, base: 10 },
  { min: 1000, base: 100 },
  { min: 10000, base: 1000 },
  { min: 100000, base: 10000 },
  { min: 1000000, base: 100000 },
  { min: 10000000, base: 1000000 },
  { min: 100000000, base: 10000000 }
];

/**
 * Calculates charts min and max values for Y axis
 */
export function getMinMax({ datasets, config }) {
  // If data is not array with numbers get y axis values from config?.parsing?.yAxisKey field
  const getChartYData = data =>
    config?.parsing?.yAxisKey
      ? _.map(data, i => i[config?.parsing?.yAxisKey])
      : data;

  const flatData = _.flatten([...datasets.map(i => getChartYData(i.data))]);

  const maxValue = _.maxBy(flatData) | 0;
  const minValue = _.min(flatData) | 0;

  const baseValue = Math.max(maxValue, Math.abs(minValue));

  const base = _.get(
    baseList.reverse().find(i => i.min < baseValue),
    "base",
    1
  );

  const isMinNegative = minValue < 0;
  const isMaxPositive = maxValue > 0;

  /** Get stepSize i.e size of each square in the chart */
  const parts = splitToParts(Math.abs(maxValue), 6, base);
  let stepSize = _.max(parts) || 1;
  const minParts = splitToParts(Math.abs(minValue), 6, base);
  const minStepSize = _.max(minParts) || 1;
  stepSize = minStepSize > stepSize ? minStepSize : stepSize;

  // 1.4 = ~40% above max value. Applies only for values bigger than 10
  let maxY = isMaxPositive
    ? Math.ceil((Math.abs(maxValue) * 1.4) / stepSize) * stepSize
    : 0;
  let minY = isMinNegative
    ? Math.ceil((Math.abs(minValue) * 1.4) / stepSize) * stepSize
    : 0;

  maxY =
    (isMaxPositive ? 1 : -1) * getValueOrChartDefaultMinMax(Math.abs(maxY));
  minY =
    (isMinNegative ? -1 : 1) * getValueOrChartDefaultMinMax(Math.abs(minY));

  // If no values set minY and maxY to default
  if ((isNaN(maxValue) || !maxValue) && (isNaN(minValue) || !minValue)) {
    maxY = getValueOrChartDefaultMinMax(1);
    minY = 0;
  }

  return {
    minY,
    maxY,
    stepSize
  };
}

/**
 * Return default chart config merged with the provided config
 * @param data
 * @param data.chartData Chart data
 * @param data.config Optional configuration to override defaults
 * @param data.simple If True Create chart without labels and legend
 */
export function defaultChartConfig({ chartData, config, simple = false }) {
  let defaultConfig = {
    responsive: true,
    maintainAspectRatio: false,
    beginAtZero: true,
    tension: 0.5,
    // Disable hide animations
    transitions: {
      hide: {
        animations: {
          visible: () => {
            return null;
          }
        }
      }
    }
  };

  if (simple) {
    defaultConfig = _.merge(defaultConfig, {
      scales: {
        y: { ticks: { display: false } },
        x: { ticks: { display: false } }
      },
      legend: {
        display: false
      }
    });
  }

  const { minY, maxY, stepSize } = getMinMax({
    datasets: chartData.datasets,
    config
  });

  // https://github.com/chartjs/Chart.js/issues/2438
  defaultConfig = _.merge(
    defaultConfig,
    _.set({}, "scales.y", {
      suggestedMax: maxY,
      suggestedMin: minY,
      ticks: {
        stepSize: stepSize
      }
    })
  );

  return _.merge(defaultConfig, config);
}
